import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DataTable,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Toastify,
  Checkbox,
  Badge,
  SpinnerLoader,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Label,
  Input,
  Search,
  MultiSelectCheckboxTag,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import {
  Check,
  Pause,
  Trash2,
  RefreshCw,
  Edit,
  Users2,
  ShieldCheck,
  ShieldOff,
  CheckCircle,
  XCircle,
  MoreVertical,
  UserPlus,
  UserMinus,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PasswordValidator from "password-validator";
import validator from "validator";

import "./users.styles.scss";


import { useThrottledFn } from "@lift-admin/utils/throttle";
import { debounce } from "@lift-admin/utils/debounce";
import apiClient from "@lift-admin/utils/apiClient";
import { useAuthContext } from "@lift-admin/utils/provider";
import { getIsLoggingOut } from "@lift-admin/utils";
import {
  buildSummary,
  buildDetails,
  diffFields,
  type FieldSpec,
} from "@lift-admin/utils/logChangeFormatter";
import { actionLog } from "../../App";
import apiClientDrawerV2, {
  invalidateUserSidebarCache,
} from "@lift-admin/utils/apiClientDrawerV2";
import { isExcludedFromDefaultApplication } from "../../lib/excludeFromDefaultApplication";
import { getAppChannelErrors, sanitizeAccessSelection } from "./permissionRules";

const emptyPermissionSnapshot = () => ({
  brands: [] as string[],
  channels: [] as string[],
  applications: [] as string[],
  items: [] as string[],
});

const sameIdSet = (a: string[] = [], b: string[] = []) => {
  if (a.length !== b.length) return false;
  const setA = new Set(a.map(String));
  return b.every((id) => setA.has(String(id)));
};

interface User {
  id: number;
  name: string;
  email: string;
  image: string;
  verified: boolean;
  created_at: string;
  admin_verified: number;
  is_active: number;
  team: boolean;
  is_admin: boolean;
  track_usage: boolean;
  default_application_id?: string | null;
}

const USER_FILTER_IDS = [
  "pending",
  "allowed",
  "on-hold",
  "deleted",
  "team",
  "admin",
] as const;

type UserFilterId = (typeof USER_FILTER_IDS)[number];

const USER_FILTER_ID_SET = new Set<string>(USER_FILTER_IDS);

function isUserFilterId(v: string): v is UserFilterId {
  return USER_FILTER_ID_SET.has(v);
}

const USER_TYPE_FILTER_OPTIONS: { label: string; value: string }[] = [
  { label: "Pending", value: "pending" },
  { label: "Allowed", value: "allowed" },
  { label: "On Hold", value: "on-hold" },
  { label: "Deleted", value: "deleted" },
  { label: "Team", value: "team" },
  { label: "Admin", value: "admin" },
];

function matchesUserType(user: User, type: string): boolean {
  switch (type) {
    case "pending":
      return user.admin_verified === 0 && user.is_active !== 0;
    case "allowed":
      return user.admin_verified === 1;
    case "on-hold":
      return user.admin_verified === 2;
    case "deleted":
      return user.is_active === 0;
    case "team":
      return !!user.team;
    case "admin":
      return !!user.is_admin;
    default:
      return false;
  }
}

/** Empty or all options selected = show full list (same as former "All users"). */
function applyUserTypeFilter(list: User[], types: string[]): User[] {
  const unique = [...new Set(types.filter(isUserFilterId))];
  if (unique.length === 0 || unique.length >= USER_FILTER_IDS.length) {
    return list;
  }
  return list.filter((u) => unique.some((t) => matchesUserType(u, t)));
}

const LEGACY_TAB_TO_FILTER: Record<string, UserFilterId> = {
  pending: "pending",
  allowed: "allowed",
  "on-hold": "on-hold",
  deleted: "deleted",
};

function parseUserTypesFromSearchParams(searchParams: URLSearchParams): string[] {
  const raw = searchParams.get("types");
  if (raw) {
    const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
    return parts.filter(isUserFilterId);
  }
  const tab = searchParams.get("tab");
  if (tab === "all-users" || !tab) return [];
  const mapped = tab ? LEGACY_TAB_TO_FILTER[tab] : undefined;
  return mapped ? [mapped] : [];
}

// Default application and its channels are assigned to new users (matches backend UserPermissionsService.initializeUser)
const DEFAULT_CREATE_USER_APPLICATION_ID = "df395e1f-2c33-11f0-9ce6-42010a400fe5";

const Users = ({
  signupSource,
  match = "exclude",
  title = "Users",
}: {
  signupSource?: string;
  match?: "include" | "exclude";
  title?: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [userTypeFilter, setUserTypeFilter] = useState<string[]>(() =>
    parseUserTypesFromSearchParams(searchParams)
  );
  const userTypeFilterRef = useRef(userTypeFilter);
  userTypeFilterRef.current = userTypeFilter;

  const [users, setUsers] = useState<User[]>([]);
  const [usersBackup, setUsersBackup] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchedUsers, setSearchedUsers] = useState<string>("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [newUserDrawerOpen, setNewUserDrawerOpen] = useState<boolean>(false);
  const [brandListData, setBrandListData] = useState<any[]>([]);
  const [brandsList, setBrandsList] = useState<any>([]);
  const [allBrandsList, setAllBrandsList] = useState<any>([]);
  const [newUserSelectedBrand, setNewUserSelectedBrand] = useState<any>([]);
  const [newUserSelectedChannel, setNewUserSelectedChannel] = useState<any>([]);
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [nameError, setNameError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [disableEmailEdit, setDisableEmailEdit] = useState<boolean>(false);
  const [disablePasswordEdit, setDisablePasswordEdit] = useState<boolean>(false);
  const [updatingUser, setUpdatingUser] = useState<any>();
  const [userAssignedChannels, setUserAssignedChannels] = useState<any>(null);
  const [brandsPerUser, setBrandsPerUser] = useState<any>([]);
  const [orgsPerUser, setOrgsPerUser] = useState<any>([]);
  const [channelList, setChannelList] = useState<any>([]);
  const [applicationList, setApplicationList] = useState<any>([]);
  const [userAssignedApplications, setUserAssignedApplications] = useState<any>(null);
  const [newUserSelectedApplication, setNewUserSelectedApplication] = useState<any>([]);
  const [defaultApplicationId, setDefaultApplicationId] = useState<string>("");
  const [loadingApplications, setLoadingApplications] = useState<boolean>(false);
  const [restrictedItemList, setRestrictedItemList] = useState<any[]>([]);
  const [userAssignedRestrictedItems, setUserAssignedRestrictedItems] = useState<any[]>([]);
  const [newUserSelectedRestrictedItems, setNewUserSelectedRestrictedItems] = useState<any>({ granted: [] });
  const [loadingRestrictedItems, setLoadingRestrictedItems] = useState<boolean>(false);
  const [channelAccessWarnings, setChannelAccessWarnings] = useState<any>([]);
  const [applicationAccessWarnings, setApplicationAccessWarnings] = useState<any>([]);
  const [addUser, setAddUser] = useState<boolean>(false);
  /** Snapshot of permissions when the edit sheet opened — used for accurate add/remove logs. */
  const initialPermissionsRef = useRef(emptyPermissionSnapshot());

  const [aboutToDeleteUsers, setAboutToDeleteUsers] = useState<User | null>(null);
  const [hideDeleteUsersProgressDialog, setHideDeleteUsersProgressDialog] = useState<boolean>(true);

  const [loadList, setLoadList] = useState<boolean>(true);
  const [loadingBrands, setLoadingBrands] = useState<boolean>(false);
  const [loadingChannels, setLoadingChannels] = useState<boolean>(false);
  const [loaderAdd, setLoaderAdd] = useState<boolean>(false);
  const [loaderUpdate, setLoaderUpdate] = useState<boolean>(false);
  const [loaderAddToTeam, setLoaderAddToTeam] = useState<{ [key: number]: boolean }>({});
  const [loaderAdmin, setLoaderAdmin] = useState<{ [key: number]: boolean }>({});
  const [loaderTracking, setLoaderTracking] = useState<{ [key: number]: boolean }>({});
  const [loaderUsers, setLoaderUsers] = useState<boolean>(false);

  const [confirmTeamDialog, setConfirmTeamDialog] = useState(false);
  const [confirmAdminDialog, setConfirmAdminDialog] = useState(false);
  const [confirmAllowDialog, setConfirmAllowDialog] = useState(false);
  const [confirmHoldDialog, setConfirmHoldDialog] = useState(false);
  const [confirmUpdateSheetDialog, setConfirmUpdateSheetDialog] = useState(false);
  const [confirmCreateSheetDialog, setConfirmCreateSheetDialog] = useState(false);
  const [userToModify, setUserToModify] = useState<User | null>(null);
  const [userToAllow, setUserToAllow] = useState<User | null>(null);
  const [userToHold, setUserToHold] = useState<User | null>(null);
  const [allowDialogLoading, setAllowDialogLoading] = useState(false);
  const [holdDialogLoading, setHoldDialogLoading] = useState(false);
  const [teamDialogLoading, setTeamDialogLoading] = useState(false);
  const [adminDialogLoading, setAdminDialogLoading] = useState(false);

  const [selected, setSelected] = useState<number[]>([]);
  const [confirmBulkDialog, setConfirmBulkDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState("");
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [tableLoader, setTableLoader] = useState(false);

  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState<string>("created_at");

  const {
    createUserAsAdmin,
    canView,
    canEdit,
    canManage,
    loading: authLoading,
  } = useAuthContext();
  const [defaultBrand, setDefaultBrand] = useState<any>(null);

  // Aligns with IAM Support / packages/utils permissions (InfoCards + README):
  // Viewer → view only | Editor → view + add/update | Manager → + delete/manage | is_superadmin → full access
  const hasViewAccess = canView("user_mgmt");
  const hasEditAccess = canEdit("user_mgmt");
  const hasManageAccess = canManage("user_mgmt");

  const activeSelectableIds = useMemo(
    () => users.filter((u) => u.is_active !== 0).map((u) => u.id),
    [users]
  );

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setRowsPerPage(newPageSize);
    setPage(0);
  };

  // Format Date
  const formatDate = (timeStamp: string) => {
    if (!timeStamp) return "N/A";
    const date = new Date(timeStamp);
    return (
      (date.getMonth() + 1) +
      "/" +
      date.getDate() +
      "/" +
      date.getFullYear() +
      " " +
      date.getHours() +
      ":" +
      date.getMinutes() +
      ":" +
      date.getSeconds()
    );
  };

  const usersInitRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!hasViewAccess) {
      setLoadList(false);
      return;
    }
    if (usersInitRef.current) return;
    usersInitRef.current = true;

    void getUsers();
    if (hasEditAccess) {
      listOfBrands();
      getListOfAllChannels();
      getListOfAllApplications();
      getListOfRestrictedItems();
      listOfBrandsWithOrganizations();
    }
  }, [authLoading, hasViewAccess, hasEditAccess]);

  // Function to get all users (keeps current tab so URL and data stay in sync)
  const getUsers = async () => {
    if (!hasViewAccess) {
      setLoaderUsers(false);
      setLoadList(false);
      return;
    }
    setLoaderUsers(true);
    await apiClient
      .get("/user/v1/admin/all", {
        params: signupSource ? { signup_source: signupSource, match } : undefined,
      })
      .then((res) => {
        setLoadList(false);

        const sorted = res.data.response.sort(customSortBasedOnDate).reverse();
        setUsersBackup(sorted);

        const applyTypeFilter = (list: User[]) =>
          applyUserTypeFilter(list, userTypeFilterRef.current);

        // Re-apply search filter if user had a search term (e.g. after team/admin update)
        if (searchText.trim()) {
          const q = searchText.toLowerCase();
          const filteredByTab = applyTypeFilter(sorted);
          const filtered = filteredByTab.filter(
            (user: any) =>
              user.name?.toLowerCase().includes(q) ||
              user.email?.toLowerCase().includes(q) ||
              user.brand?.toLowerCase().includes(q)
          );
          setUsers(filtered);
        } else {
          setUsers(applyTypeFilter(sorted));
        }

        setLoaderUsers(false);
      })
      .catch((error) => {
        if (!getIsLoggingOut()) {
          Toastify("error", error.response?.data?.message || "Error fetching users");
        }
        console.log(error);
        setLoaderUsers(false);
      });
  };

  // Function to sort users data based on their date
  const customSortBasedOnDate = (a: any, b: any) =>
    new Date(a.id).getTime() - new Date(b.id).getTime();

  // Function to allow user
  const allowUser = async (user: User) => {
    if (!hasEditAccess) {
      Toastify("error", "You do not have permission to allow users");
      return;
    }
    try {
      const previousStatus =
        user.admin_verified === 2
          ? "On Hold"
          : user.admin_verified === 0
            ? "Pending"
            : "Allowed";
      const res = await apiClient.post(`/user/v1/admin/verify/${user.id}`);
      const changes = [
        { label: "Access Status", from: previousStatus, to: "Allowed" },
      ];
      const desc = {
        message: buildSummary("User", user.email, "updated", changes.length),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "user_management",
        action: "allow_user",
        userId: user.id,
        userEmail: user.email,
        changes,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      Toastify("success", "User allowed");
    } catch (error: any) {
      if (error.response) {
        Toastify("error", error.response.data.message);
      } else {
        Toastify("error", "Something went wrong !!");
      }
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Failed to allow user "${user.email}"`,
          status: error?.response?.status || 500,
          method: "POST",
          url: `/user/v1/admin/verify/${user.id}`,
          category: "user_management",
          action: "allow_user",
          userId: user.id,
          userEmail: user.email,
          error: error?.response?.data?.message || error?.message,
        }),
        true
      );
    }
    getUsers();
  };

  const confirmAllowUser = async () => {
    if (!userToAllow) return;
    setAllowDialogLoading(true);
    try {
      await allowUser(userToAllow);
      setConfirmAllowDialog(false);
      setUserToAllow(null);
    } finally {
      setAllowDialogLoading(false);
    }
  };

  // Function to put user on hold
  const putUserOnHold = async (user: User) => {
    if (!hasEditAccess) {
      Toastify("error", "You do not have permission to put users on hold");
      return;
    }
    try {
      const previousStatus =
        user.admin_verified === 1
          ? "Allowed"
          : user.admin_verified === 0
            ? "Pending"
            : "On Hold";
      const res = await apiClient.put(`/user/v1/admin/hold/${user.id}`);
      const changes = [
        { label: "Access Status", from: previousStatus, to: "On Hold" },
      ];
      const desc = {
        message: buildSummary("User", user.email, "updated", changes.length),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "user_management",
        action: "hold_user",
        userId: user.id,
        userEmail: user.email,
        changes,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      Toastify("success", "User on hold");
    } catch (error: any) {
      if (error.response) {
        Toastify("error", error.response.data.message);
      } else {
        Toastify("error", "Something went wrong !!");
      }
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Failed to put user "${user.email}" on hold`,
          status: error?.response?.status || 500,
          method: "PUT",
          url: `/user/v1/admin/hold/${user.id}`,
          category: "user_management",
          action: "hold_user",
          userId: user.id,
          userEmail: user.email,
          error: error?.response?.data?.message || error?.message,
        }),
        true
      );
    }
    getUsers();
  };

  const confirmHoldUser = async () => {
    if (!userToHold) return;
    setHoldDialogLoading(true);
    try {
      await putUserOnHold(userToHold);
      setConfirmHoldDialog(false);
      setUserToHold(null);
    } finally {
      setHoldDialogLoading(false);
    }
  };

  // Function to delete user
  const deleteUser = async (user: User) => {
    if (!hasManageAccess) {
      Toastify("error", "You do not have permission to delete users");
      return;
    }
    setHideDeleteUsersProgressDialog(false);
    try {
      const res = await apiClient.delete(`/user/v1/admin/delete/${user.id}`);
      const desc = {
        message: buildSummary('User', user.email, 'deleted'),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: 'user_management',
        action: 'delete_user',
        userId: user.id,
        userEmail: user.email,
        details: buildDetails(
          user,
          [
            { key: 'name', label: 'Name' },
            { key: 'email', label: 'Email' },
          ],
          { skipEmpty: true }
        ),
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      Toastify("success", "User deleted");
      setAboutToDeleteUsers(null);
      setHideDeleteUsersProgressDialog(true);
    } catch (error: any) {
      if (error.response) {
        Toastify("error", error.response.data.message);
      } else {
        Toastify("error", "Something went wrong !!");
      }
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Failed to delete user "${user.email}"`,
          status: error?.response?.status || 500,
          method: "DELETE",
          url: `/user/v1/admin/delete/${user.id}`,
          category: "user_management",
          action: "delete_user",
          userId: user.id,
          userEmail: user.email,
          error: error?.response?.data?.message || error?.message,
        }),
        true
      );
    }
    getUsers();
  };

  const throttleDeleteUser = useThrottledFn(deleteUser, 1000);

  const applyTypesToDisplayedUsers = (types: string[], backup: User[], search: string) => {
    if (!search.trim()) {
      setUsers(applyUserTypeFilter(backup, types));
      return;
    }
    const q = search.toLowerCase();
    const base = applyUserTypeFilter(backup, types);
    setUsers(
      base.filter(
        (user: any) =>
          user.name?.toLowerCase().includes(q) ||
          user.email?.toLowerCase().includes(q) ||
          user.brand?.toLowerCase().includes(q)
      )
    );
  };

  const onUserTypesFilterChange = (selectedValues: string[]) => {
    setUserTypeFilter(selectedValues);
    setPage(0);
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    if (
      selectedValues.length === 0 ||
      selectedValues.length >= USER_FILTER_IDS.length
    ) {
      next.delete("types");
    } else {
      next.set("types", selectedValues.join(","));
    }
    setSearchParams(next, { replace: true });
    applyTypesToDisplayedUsers(selectedValues, usersBackup, searchText);
  };

  // Function to get list of brands
  const listOfBrands = () => {
    apiClient
      .get(`/brand/v1/admin/brands/all`)
      .then((res) => {
        let brandOptionsTag: any = [];
        let br: any = [];
        for (let i = 0; i < res?.data?.response?.length; i++) {
          brandOptionsTag.push(res?.data?.response[i]["name"]);
          br.push(res?.data?.response[i]["name"]);
        }

        setBrandListData(br);
        setBrandsList(brandOptionsTag);
      })
      .catch((error) => {
        if (!getIsLoggingOut()) {
          Toastify("error", error.response?.data?.message || "Error fetching brands");
        }
        console.error(error);
      });
  };

  const listOfBrandsWithOrganizations = async () => {
    try {
      const res = await apiClient.get(`/brand/v1/organizations/brandsAndOrganizations/all`);
      const filteredOrganizations = res.data.response
        .map((org: any) => ({
          ...org,
          brands: org.brands,
        }))
        .filter((org: any) => org.brands.length > 0);

      setAllBrandsList(filteredOrganizations);
    } catch (error: any) {
      console.error("Error retrieving brands with organizations:", error.response ? error.response.data : error.message);
      if (!getIsLoggingOut()) {
        Toastify("error", "Failed to retrieve brands with organizations");
      }
    }
  };

  const getBrandsforUser = async (userId: number) => {
    setLoadingBrands(true);
    try {
      const res = await apiClient.get(`/user/v1/user_permissions/${userId}/brands`);
      if (res.data.response && Array.isArray(res.data.response) && res.data.response.length) {
        setDefaultBrand(res.data.response[0]);
        const sortedBrands = res.data.response.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name);
        });
        const brandIds = sortedBrands
          .filter((b: any) => b?.id != null)
          .map((b: any) => String(b.id));
        setBrandsPerUser(sortedBrands);
        setNewUserSelectedBrand({ granted: brandIds });
        initialPermissionsRef.current = {
          ...initialPermissionsRef.current,
          brands: brandIds,
        };
      } else {
        setBrandsPerUser([]);
        setNewUserSelectedBrand({ granted: [] });
        initialPermissionsRef.current = {
          ...initialPermissionsRef.current,
          brands: [],
        };
      }
    } catch (error) {
      console.error("Error fetching brands for user:", error);
      Toastify("error", "Error fetching Assigned brands");
      setBrandsPerUser([]);
      setNewUserSelectedBrand({ granted: [] });
      initialPermissionsRef.current = {
        ...initialPermissionsRef.current,
        brands: [],
      };
    } finally {
      setLoadingBrands(false);
    }
  };

  const getOrgsPerUser = () => {
    const brandsPerUserIds = brandsPerUser?.map((brand: any) => brand.id);

    const orgsPerUserResult = allBrandsList
      ?.filter((org: any) => {
        const orgBrandIds = org?.brands?.map((brand: any) => brand.id);
        return orgBrandIds.every((id: any) => brandsPerUserIds.includes(id));
      })
      .map((org: any) => ({
        id: org.id,
        name: org.name,
        brands: org.brands,
      }));

    setOrgsPerUser(orgsPerUserResult);
  };

  useEffect(() => {
    if (brandsPerUser) {
      getOrgsPerUser();
    }
  }, [brandsPerUser]);

  // Validate channel access when brands or channels change
  useEffect(() => {
    if (newUserSelectedBrand?.granted && newUserSelectedChannel?.granted) {
      const selectedBrandData = newUserSelectedBrand.granted
        .map((brandId: any) => {
          let brand = brandsPerUser?.find((b: any) => b.id === brandId);
          if (!brand) {
            for (const org of allBrandsList || []) {
              brand = org.brands?.find((b: any) => b.id === brandId);
              if (brand) break;
            }
          }
          return brand;
        })
        .filter(Boolean);

      validateChannelAccess(selectedBrandData, newUserSelectedChannel.granted);
    } else {
      setChannelAccessWarnings([]);
    }
  }, [newUserSelectedBrand?.granted, newUserSelectedChannel?.granted, channelList]);

  // Validate application access when brands or applications change
  useEffect(() => {
    if (newUserSelectedBrand?.granted && newUserSelectedApplication?.granted) {
      const selectedBrandData = newUserSelectedBrand.granted
        .map((brandId: any) => {
          let brand = brandsPerUser?.find((b: any) => b.id === brandId);
          if (!brand) {
            for (const org of allBrandsList || []) {
              brand = org.brands?.find((b: any) => b.id === brandId);
              if (brand) break;
            }
          }
          return brand;
        })
        .filter(Boolean);

      validateApplicationAccess(selectedBrandData, newUserSelectedApplication.granted);
    } else {
      setApplicationAccessWarnings([]);
    }
  }, [newUserSelectedBrand?.granted, newUserSelectedApplication?.granted, applicationList]);

  // Only show/select channels after application selection
  useEffect(() => {
    const selectedAppIds = (userAssignedApplications || [])
      .filter((a: any) => a?.id != null)
      .map((a: any) => String(a.id));

    if (selectedAppIds.length === 0) {
      if (Array.isArray(userAssignedChannels) && userAssignedChannels.length > 0) {
        setUserAssignedChannels([]);
        setNewUserSelectedChannel({ granted: [] });
      }
      return;
    }

    if (!Array.isArray(userAssignedChannels) || userAssignedChannels.length === 0) return;

    const filtered = userAssignedChannels.filter((ch: any) =>
      selectedAppIds.includes(String(ch?.application_id))
    );
    if (filtered.length !== userAssignedChannels.length) {
      setUserAssignedChannels(filtered);
      setNewUserSelectedChannel({
        granted: filtered.filter((c: any) => c?.id != null).map((c: any) => String(c.id)),
      });
    }
  }, [userAssignedApplications, channelList]);

  // Sync type filter from URL (back/forward, shared links with ?types= or legacy ?tab=)
  useEffect(() => {
    const parsed = parseUserTypesFromSearchParams(searchParams);
    const norm = (a: string[]) => [...a].sort().join("|");
    if (norm(parsed) === norm(userTypeFilter)) return;
    setUserTypeFilter(parsed);
    setPage(0);
    if (usersBackup.length === 0) return;
    applyTypesToDisplayedUsers(parsed, usersBackup, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to URL changes; avoid loops with userTypeFilter
  }, [searchParams]);

  const validateChannelAccess = (brands: any[], channelIds: number[]) => {
    const warnings: any[] = [];
    // Add validation logic here
    setChannelAccessWarnings(warnings);
  };

  const validateApplicationAccess = (brands: any[], applicationIds: number[]) => {
    const warnings: any[] = [];
    // Add validation logic here
    setApplicationAccessWarnings(warnings);
  };

  // Edit mode only: every granted application needs at least one of its own
  // channels. Enforced only once applications or channels differ from what the
  // user had when the drawer opened, so a legacy user that already breaks the
  // rule can still have their name or other fields saved.
  const getApplicationChannelErrors = (): string[] => {
    if (addUser) return [];
    if (loadingApplications || loadingChannels) return [];
    if (!Array.isArray(channelList) || channelList.length === 0) return [];
    const initial = initialPermissionsRef.current;
    const appIds = Array.isArray(newUserSelectedApplication?.granted)
      ? newUserSelectedApplication.granted.map(String)
      : [...initial.applications];
    const channelIds = Array.isArray(newUserSelectedChannel?.granted)
      ? newUserSelectedChannel.granted.map(String)
      : [...initial.channels];
    if (
      sameIdSet(initial.applications, appIds) &&
      sameIdSet(initial.channels, channelIds)
    ) {
      return [];
    }
    const appName = (appId: string) => {
      const app = (applicationList || []).find(
        (a: any) => a?.id != null && String(a.id) === appId
      );
      return app?.name ? String(app.name) : appId;
    };
    return getAppChannelErrors(appIds, channelIds, channelList, appName);
  };
  const applicationChannelErrors = getApplicationChannelErrors();
  const applicationChannelError = applicationChannelErrors.join(" ");

  const getListOfAllChannels = async (): Promise<any[]> => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await apiClientDrawerV2.get("/admin/channels", {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        },
      });
      const channels =
        res.data?.map((channel: any) => ({
          id: channel?.id,
          name: channel?.name,
          application_id: channel?.application_id,
          application_name: channel?.application_name,
          access: channel?.access,
        })) ?? [];
      setChannelList(channels);
      return channels;
    } catch (error) {
      console.error("Error fetching channels:", error);
      if (!getIsLoggingOut()) {
        Toastify("error", "Error fetching channels");
      }
      return [];
    }
  };

  const getListOfAllApplications = async (): Promise<any[]> => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await apiClientDrawerV2.get("/admin/applications", {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        },
      });
      const applications =
        res.data?.map((application: any) => ({
          id: application?.id,
          name: application?.name,
          access: application?.access,
          exclude_from_default_application:
            application?.exclude_from_default_application,
          metadata: application?.metadata,
        })) ?? [];
      setApplicationList(applications);
      return applications;
    } catch (error) {
      console.error("Error fetching applications:", error);
      Toastify("error", "Error fetching applications");
      return [];
    }
  };

  const getListOfRestrictedItems = async (): Promise<any[]> => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await apiClientDrawerV2.get("/admin/items", {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        },
      });
      const items = (Array.isArray(res.data) ? res.data : [])
        .filter((item: any) => item?.is_restricted === true && item?.id != null)
        .map((item: any) => ({
          id: item.id,
          display_name: item.display_name,
          channel_id: item.channel_id,
        }));
      setRestrictedItemList(items);
      return items;
    } catch (error) {
      console.error("Error fetching restricted items:", error);
      if (!getIsLoggingOut()) {
        Toastify("error", "Error fetching restricted drawer items");
      }
      return [];
    }
  };

  // Function to handle submit for new user
  const handleSubmit = async () => {
    if (!hasEditAccess) {
      Toastify("error", "You do not have permission to create users");
      return;
    }
    setLoaderAdd(true);
    try {
      let passwordSchema = new PasswordValidator();
      passwordSchema
        .is().min(6)
        .is().max(15)
        .has().uppercase(1)
        .has().lowercase(1)
        .has().digits(1)
        .has().not().spaces();

      if (name == null || name === "") {
        setNameError("Required!");
        setEmailError("");
        setPasswordError("");
        setConfirmPasswordError("");
        return;
      } else if (email == null || email === "") {
        setNameError("");
        setEmailError("Required!");
        setPasswordError("");
        setConfirmPasswordError("");
        return;
      } else if (password == null || password === "") {
        setNameError("");
        setEmailError("");
        setPasswordError("Required!");
        setConfirmPasswordError("");
        return;
      } else if (confirmPassword == null || confirmPassword === "") {
        setNameError("");
        setEmailError("");
        setPasswordError("");
        setConfirmPasswordError("Required!");
        return;
      } else if (!validator.isEmail(email)) {
        setNameError("");
        setEmailError("Invalid Email!");
        setPasswordError("");
        setConfirmPasswordError("");
        return;
      } else if (!passwordSchema.validate(password)) {
        setNameError("");
        setEmailError("");
        setPasswordError(
          "Password should be of length 6 - 15 and should contain at least 1 uppercase, 1 lowercase, 1 digit, and no spaces."
        );
        setConfirmPasswordError("");
        return;
      } else if (password !== confirmPassword) {
        setNameError("");
        setEmailError("");
        setPasswordError("");
        setConfirmPasswordError("Doesn't match password");
        return;
      } else {
        setShowLoading(true);
        setNameError("");
        setEmailError("");
        setPasswordError("");
        setConfirmPasswordError("");

        let brands: string = "LIFT Demo Brand";

        const id = await createUserAsAdmin(email, password);

        const requestBody = {
          name: name,
          email: email,
          brand: brands,
          uuid: id,
        };

        const response = await apiClient.post("/user/v1/admin/user/add", requestBody);

        setShowLoading(false);
        setNewUserDrawerOpen(false);
        getUsers();

        const resolveBrandNameForCreate = (brandId: any): string => {
          for (const org of allBrandsList || []) {
            const brand = org.brands?.find(
              (b: any) => String(b.id) === String(brandId)
            );
            if (brand) return brand.name;
          }
          return String(brandId);
        };
        const brandNames = (newUserSelectedBrand?.granted || [])
          .map(resolveBrandNameForCreate)
          .join(", ");
        const applicationNames = (newUserSelectedApplication?.granted || [])
          .map(
            (id: any) =>
              applicationList?.find((a: any) => String(a.id) === String(id))
                ?.name || String(id)
          )
          .join(", ");
        const channelNames = (newUserSelectedChannel?.granted || [])
          .map((id: any) => {
            const ch = channelList?.find(
              (c: any) => String(c.id) === String(id)
            );
            if (!ch) return String(id);
            return ch.application_name
              ? `${ch.name} (${ch.application_name})`
              : ch.name || String(id);
          })
          .join(", ");
        const createDetails = buildDetails(
          {
            name,
            email,
            brand: brands,
            brands: brandNames,
            applications: applicationNames,
            channels: channelNames,
            defaultApplication: resolveApplicationName(defaultApplicationId),
          },
          [
            { key: "name", label: "Name" },
            { key: "email", label: "Email" },
            { key: "brand", label: "Default Brand" },
            { key: "brands", label: "Brand Permissions" },
            { key: "applications", label: "Application Permissions" },
            { key: "channels", label: "Channel Permissions" },
            { key: "defaultApplication", label: "Default Application" },
          ] as FieldSpec[],
          { skipEmpty: true }
        );
        const desc = {
          message: buildSummary("User", name || email, "created"),
          status: response.status,
          method: response.config.method,
          url: response.config.url,
          category: "user_management",
          action: "create_user",
          userEmail: email,
          details: createDetails,
        };

        await actionLog?.actionLogTrack(JSON.stringify(desc), false);
        Toastify("success", "User added");
      }
    } catch (error: any) {
      setShowLoading(false);
      if (error?.response) {
        setEmailError(error.response.data.message);
        Toastify("error", error.response.data.message);
      } else {
        Toastify("error", error?.message || "Something went wrong!!");
      }
      console.error(error);
      await actionLog?.actionLogTrack(JSON.stringify(error), true);
    } finally {
      setLoaderAdd(false);
    }
  };

  // Each helper clears the user's sidebar-v3 cache in a `finally` (the write may have
  // committed even if the request errors). handleUpdate passes skipCacheInvalidate and
  // invalidates once at the end instead.
  const handleGrantBrandPermission = async (
    userID: any,
    grantedBrands: any,
    options?: { silent?: boolean; skipCacheInvalidate?: boolean }
  ) => {
    try {
      const response = await apiClient.post(
        `/user/v1/user_permissions/${userID}/brands/grant`,
        { brandIDs: grantedBrands }
      );
      if (!options?.silent) {
        console.log("Permissions granted successfully:", response.data);
        Toastify("success", "Permissions granted successfully");
      }
    } finally {
      if (!options?.skipCacheInvalidate) {
        await invalidateUserSidebarCache(userID);
      }
    }
  };

  const handleGrantChannelPermission = async (
    userID: any,
    grantChannels: any,
    options?: { silent?: boolean; skipCacheInvalidate?: boolean }
  ) => {
    try {
      await apiClient.post(
        `/user/v1/user_permissions/${userID}/channels/grant`,
        { channels: grantChannels }
      );
    } finally {
      if (!options?.skipCacheInvalidate) {
        await invalidateUserSidebarCache(userID);
      }
    }
    if (!options?.silent) {
      Toastify("success", "Permissions granted successfully");
    }
  };

  const handleGrantApplicationPermission = async (
    userID: any,
    grantApplications: any,
    options?: { silent?: boolean; skipCacheInvalidate?: boolean }
  ) => {
    try {
      await apiClient.post(
        `/user/v1/user_permissions/${userID}/applications/grant`,
        { applications: grantApplications }
      );
    } finally {
      if (!options?.skipCacheInvalidate) {
        await invalidateUserSidebarCache(userID);
      }
    }
    if (!options?.silent) {
      Toastify("success", "Application permissions granted successfully");
    }
  };

  // Saves applications and channels together so User-Service can check that
  // every application comes with one of its channels. Falls back to the two
  // separate grants where that endpoint is not deployed yet (404).
  const handleSetUserAccess = async (
    userID: any,
    access: { applications: string[]; channels: string[] }
  ) => {
    try {
      await apiClient.put(`/user/v1/user_permissions/${userID}/access`, access);
    } catch (err: any) {
      if (err?.response?.status !== 404) throw err;
      await handleGrantChannelPermission(userID, access.channels, {
        silent: true,
        skipCacheInvalidate: true,
      });
      await handleGrantApplicationPermission(userID, access.applications, {
        silent: true,
        skipCacheInvalidate: true,
      });
    }
  };

  const handleSetDefaultApplication = async (
    userID: number,
    applicationId: string,
    options?: { skipCacheInvalidate?: boolean }
  ) => {
    try {
      await apiClient.post(
        `/user/v1/user_permissions/${userID}/applications/grant/default`,
        { defaultApplicationId: applicationId }
      );
    } finally {
      if (!options?.skipCacheInvalidate) {
        await invalidateUserSidebarCache(userID);
      }
    }
  };

  const handleGrantRestrictedItems = async (
    userID: any,
    itemIds: string[],
    options?: { silent?: boolean; skipCacheInvalidate?: boolean }
  ) => {
    if (!Array.isArray(itemIds) || itemIds.length === 0) return;
    try {
      await apiClient.post(
        `/user/v1/user_permissions/${userID}/items/grant`,
        { items: itemIds }
      );
    } finally {
      if (!options?.skipCacheInvalidate) {
        await invalidateUserSidebarCache(userID);
      }
    }
    if (!options?.silent) {
      Toastify("success", "Restricted drawer items granted successfully");
    }
  };

  const handleRevokeRestrictedItems = async (
    userID: any,
    itemIds: string[],
    options?: { silent?: boolean; skipCacheInvalidate?: boolean }
  ) => {
    if (!Array.isArray(itemIds) || itemIds.length === 0) return;
    try {
      await apiClient.post(
        `/user/v1/user_permissions/${userID}/items/revoke`,
        { items: itemIds }
      );
    } finally {
      if (!options?.skipCacheInvalidate) {
        await invalidateUserSidebarCache(userID);
      }
    }
    if (!options?.silent) {
      Toastify("success", "Restricted drawer items revoked successfully");
    }
  };

  const resolveBrandName = (brandId: any): string => {
    for (const org of allBrandsList || []) {
      const brand = org.brands?.find(
        (b: any) => String(b.id) === String(brandId)
      );
      if (brand) return brand.name;
    }
    const fromAssigned = brandsPerUser?.find(
      (b: any) => String(b.id) === String(brandId)
    );
    return fromAssigned?.name || `ID ${brandId}`;
  };

  const resolveChannelName = (channelId: any): string => {
    const ch = channelList?.find(
      (c: any) => String(c.id) === String(channelId)
    );
    if (!ch) return `ID ${channelId}`;
    return ch.application_name
      ? `${ch.name} (${ch.application_name})`
      : ch.name || `ID ${channelId}`;
  };

  const resolveApplicationName = (applicationId: any): string => {
    const app = applicationList?.find(
      (a: any) => String(a.id) === String(applicationId)
    );
    return app?.name || `ID ${applicationId}`;
  };

  const resolveRestrictedItemName = (itemId: any): string => {
    const item = restrictedItemList?.find(
      (i: any) => String(i.id) === String(itemId)
    );
    return item?.display_name || `ID ${itemId}`;
  };

  const handleUpdate = async () => {
    if (!hasEditAccess) {
      Toastify("error", "You do not have permission to update users");
      return;
    }
    if (applicationChannelErrors.length > 0) {
      Toastify("error", applicationChannelError);
      return;
    }
    if (name == null || name === "") {
      setNameError("Required!");
      setEmailError("");
      setPasswordError("");
      setConfirmPasswordError("");
      return;
    }
    setNameError("");
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");
    setShowLoading(true);
    setLoaderAdd(true);

    const errors: string[] = [];
    const initial = initialPermissionsRef.current;
    const nextBrandIds = Array.isArray(newUserSelectedBrand?.granted)
      ? newUserSelectedBrand.granted.map(String)
      : [...initial.brands];
    const nextChannelIds = Array.isArray(newUserSelectedChannel?.granted)
      ? newUserSelectedChannel.granted.map(String)
      : [...initial.channels];
    const nextAppIds = Array.isArray(newUserSelectedApplication?.granted)
      ? newUserSelectedApplication.granted.map(String)
      : [...initial.applications];
    const nextItemIds = Array.isArray(newUserSelectedRestrictedItems?.granted)
      ? newUserSelectedRestrictedItems.granted.map(String)
      : [...initial.items];

    const brandsChanged = !sameIdSet(initial.brands, nextBrandIds);
    const channelsChanged = !sameIdSet(initial.channels, nextChannelIds);
    const applicationsChanged = !sameIdSet(initial.applications, nextAppIds);
    const initialDefaultApplicationId = String(updatingUser?.default_application_id ?? "");
    const defaultApplicationChanged =
      defaultApplicationId !== initialDefaultApplicationId;
    const itemsChanged = !sameIdSet(initial.items, nextItemIds);

    const initialItemSet = new Set(initial.items.map(String));
    const nextItemSet = new Set(nextItemIds.map(String));
    const itemsToGrant = nextItemIds.filter((id: string) => !initialItemSet.has(id));
    const itemsToRevoke = initial.items.filter((id: string) => !nextItemSet.has(String(id)));

    // Set once any permission write is attempted; the sidebar-v3 cache is then
    // cleared a single time in the outer `finally` rather than after each step.
    let permissionsTouched = false;

    try {
      const requestBody = { name };
      let applicationPermissionsReady = !applicationsChanged;

      // Grant only when the selection actually changed (replace semantics, including clears)
      if (brandsChanged) {
        permissionsTouched = true;
        try {
          await handleGrantBrandPermission(updatingUser.id, nextBrandIds, {
            silent: true,
            skipCacheInvalidate: true,
          });
        } catch (err: any) {
          errors.push(
            err?.response?.data?.message || "Failed to grant brand permissions"
          );
        }
      }

      if (channelsChanged || applicationsChanged) {
        permissionsTouched = true;
        try {
          await handleSetUserAccess(
            updatingUser.id,
            sanitizeAccessSelection(nextAppIds, nextChannelIds, channelList, applicationList)
          );
          applicationPermissionsReady = true;
        } catch (err: any) {
          errors.push(
            err?.response?.data?.message ||
              "Failed to update application and channel permissions"
          );
        }
      }

      if (defaultApplicationChanged && defaultApplicationId) {
        if (!nextAppIds.includes(defaultApplicationId)) {
          errors.push("Default application must be one of the user's applications");
        } else if (applicationPermissionsReady) {
          permissionsTouched = true;
          try {
            await handleSetDefaultApplication(
              updatingUser.id,
              defaultApplicationId,
              { skipCacheInvalidate: true }
            );
          } catch (err: any) {
            errors.push(
              err?.response?.data?.message ||
                "Failed to update default application"
            );
          }
        }
      }

      // Restricted items use union/filter grant+revoke (not replace-via-grant)
      if (itemsChanged) {
        permissionsTouched = true;
        try {
          if (itemsToGrant.length > 0) {
            await handleGrantRestrictedItems(updatingUser.id, itemsToGrant, {
              silent: true,
              skipCacheInvalidate: true,
            });
          }
          if (itemsToRevoke.length > 0) {
            await handleRevokeRestrictedItems(updatingUser.id, itemsToRevoke, {
              silent: true,
              skipCacheInvalidate: true,
            });
          }
        } catch (err: any) {
          errors.push(
            err?.response?.data?.message ||
              "Failed to update restricted drawer item permissions"
          );
        }
      }

      const permissionFieldSpecs: FieldSpec[] = [
        { key: "name", label: "Name" },
        {
          key: "brands",
          label: "Brand Permissions",
          type: "list",
          resolve: resolveBrandName,
        },
        {
          key: "channels",
          label: "Channel Permissions",
          type: "list",
          resolve: resolveChannelName,
        },
        {
          key: "applications",
          label: "Application Permissions",
          type: "list",
          resolve: resolveApplicationName,
        },
        {
          key: "defaultApplication",
          label: "Default Application",
          resolve: resolveApplicationName,
        },
        {
          key: "items",
          label: "Restricted Drawer Items",
          type: "list",
          resolve: resolveRestrictedItemName,
        },
      ];

      const userChanges = diffFields(
        {
          name: updatingUser.name,
          brands: initial.brands,
          channels: initial.channels,
          applications: initial.applications,
          defaultApplication: initialDefaultApplicationId,
          items: initial.items,
        },
        {
          name,
          brands: nextBrandIds,
          channels: nextChannelIds,
          applications: nextAppIds,
          defaultApplication: defaultApplicationId,
          items: nextItemIds,
        },
        permissionFieldSpecs
      );

      // Update user details
      try {
        const res = await apiClient.put(
          `/user/v1/admin/update/${updatingUser.id}`,
          requestBody
        );

        setDisableEmailEdit(false);
        setDisablePasswordEdit(false);
        setShowLoading(false);
        setNewUserDrawerOpen(false);
        getUsers();

        const user = JSON.parse(localStorage.getItem("user") || "{}");

        const desc = {
          message: buildSummary(
            "User",
            updatingUser.name || updatingUser.email,
            "updated",
            userChanges.length
          ),
          status: res.status,
          method: res.config.method,
          url: res.config.url,
          category: "user_management",
          action: "update_user",
          edited_user_id: updatingUser.id,
          edited_user_name: updatingUser.name,
          edited_user_email: updatingUser.email,
          user_id: user?.id,
          user_name: user?.name,
          changes: userChanges,
        };

        await actionLog?.actionLogTrack(JSON.stringify(desc), false);

        if (errors.length > 0) {
          await actionLog?.actionLogTrack(
            JSON.stringify({
              message: `Partial permission update failure for user "${updatingUser.email}"`,
              category: "user_management",
              action: "update_user_permissions",
              edited_user_id: updatingUser.id,
              edited_user_email: updatingUser.email,
              changes: userChanges,
              error: errors.join("; "),
            }),
            true
          );
          Toastify(
            "warning",
            "User profile updated, but some permissions failed: " + errors[0]
          );
        } else {
          Toastify("success", "User updated successfully");
        }
      } catch (error: any) {
        setShowLoading(false);
        setEmailError(error?.response?.data?.message || "");
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        // Permissions may already have been applied — include the intended diffs
        const errorDesc = {
          message: `Failed to update User (User ID: ${updatingUser.id}, User Name: "${updatingUser.name}", User Email: ${updatingUser.email}) by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
          status: error?.response?.status,
          method: "PUT",
          url: `/user/v1/admin/update/${updatingUser.id}`,
          category: "user_management",
          action: "update_user",
          edited_user_id: updatingUser.id,
          edited_user_name: updatingUser.name,
          edited_user_email: updatingUser.email,
          user_id: user?.id,
          user_name: user?.name,
          changes: userChanges,
          error: error?.response?.data?.message || error?.message,
        };
        await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);

        const allErrors = [
          ...errors,
          error?.response?.data?.message || "Failed to update user",
        ].filter(Boolean);
        Toastify("error", allErrors[0] || "Failed to update user");
      }
    } catch (error) {
      setShowLoading(false);
      Toastify(
        "error",
        errors.length > 0
          ? errors[0]
          : "An error occurred while updating permissions or user details"
      );
      console.error("Error:", error);
    } finally {
      if (permissionsTouched) {
        await invalidateUserSidebarCache(updatingUser.id);
      }
      setLoaderAdd(false);
    }
  };

  // Function to edit user
  const getChannelsforUser = async (userId: number, listOverride?: any[]) => {
    setLoadingChannels(true);
    try {
      const res = await apiClient.get(`/user/v1/user_permissions/${userId}/channels`);
      // API returns array of channel IDs, not full objects; match with channelList for names
      const assignedChannelIds = res.data?.response ?? [];
      if (!Array.isArray(assignedChannelIds)) {
        setUserAssignedChannels([]);
        setNewUserSelectedChannel({ granted: [] });
        initialPermissionsRef.current = {
          ...initialPermissionsRef.current,
          channels: [],
        };
        return;
      }
      const list = listOverride ?? channelList ?? [];
      const assignedChannels = assignedChannelIds
        .map((channelId: string | number) => {
          const full = list.find((ch: any) => ch?.id == null ? false : String(ch.id) === String(channelId));
          return full
            ? {
                id: full.id,
                name: full.name,
                application_id: full.application_id,
                application_name: full.application_name,
                access: full.access,
              }
            : null;
        })
        .filter(Boolean);
      assignedChannels.sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""));
      const channelIds = assignedChannelIds.map((id: string | number) => String(id));
      setUserAssignedChannels(assignedChannels);
      setNewUserSelectedChannel({ granted: channelIds });
      initialPermissionsRef.current = {
        ...initialPermissionsRef.current,
        channels: channelIds,
      };
    } catch (error) {
      console.error("Error fetching channels for user:", error);
      Toastify("error", "Error fetching Assigned channels");
      setUserAssignedChannels([]);
      setNewUserSelectedChannel({ granted: [] });
      initialPermissionsRef.current = {
        ...initialPermissionsRef.current,
        channels: [],
      };
    } finally {
      setLoadingChannels(false);
    }
  };

  const getApplicationsforUser = async (userId: number, listOverride?: any[]) => {
    setLoadingApplications(true);
    try {
      const res = await apiClient.get(`/user/v1/user_permissions/${userId}/applications`);
      // API returns array of application IDs, not full objects; match with applicationList for names
      const assignedAppIds = res.data?.response ?? [];
      if (!Array.isArray(assignedAppIds)) {
        setUserAssignedApplications([]);
        setNewUserSelectedApplication({ granted: [] });
        setDefaultApplicationId("");
        initialPermissionsRef.current = {
          ...initialPermissionsRef.current,
          applications: [],
        };
        return;
      }
      const list = listOverride ?? applicationList ?? [];
      const appIds = assignedAppIds.map((id: string | number) => String(id));
      const assignedApplications = assignedAppIds
        .map((appId: string | number) => {
          const full = list.find((app: any) => app?.id != null && String(app.id) === String(appId));
          return full ? { id: full.id, name: full.name } : null;
        })
        .filter(Boolean);
      assignedApplications.sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""));
      setUserAssignedApplications(assignedApplications);
      setNewUserSelectedApplication({ granted: appIds });
      setDefaultApplicationId((current) => {
        const currentApplication = list.find(
          (app: any) => String(app?.id) === String(current)
        );
        return appIds.includes(current) &&
          !isExcludedFromDefaultApplication(currentApplication)
          ? current
          : "";
      });
      initialPermissionsRef.current = {
        ...initialPermissionsRef.current,
        applications: appIds,
      };
    } catch (error) {
      console.error("Error fetching applications for user:", error);
      Toastify("error", "Error fetching Assigned applications");
      setUserAssignedApplications([]);
      setNewUserSelectedApplication({ granted: [] });
      setDefaultApplicationId("");
      initialPermissionsRef.current = {
        ...initialPermissionsRef.current,
        applications: [],
      };
    } finally {
      setLoadingApplications(false);
    }
  };

  const getRestrictedItemsForUser = async (userId: number, listOverride?: any[]) => {
    setLoadingRestrictedItems(true);
    try {
      const res = await apiClient.get(
        `/user/v1/user_permissions/${userId}/items`
      );
      const assignedItemIds = res.data?.response ?? [];
      if (!Array.isArray(assignedItemIds)) {
        setUserAssignedRestrictedItems([]);
        setNewUserSelectedRestrictedItems({ granted: [] });
        initialPermissionsRef.current = {
          ...initialPermissionsRef.current,
          items: [],
        };
        return;
      }
      const list = listOverride ?? restrictedItemList ?? [];
      const itemIds = assignedItemIds.map((id: string | number) => String(id));
      const assignedItems = assignedItemIds
        .map((itemId: string | number) => {
          const full = list.find(
            (item: any) => item?.id != null && String(item.id) === String(itemId)
          );
          return full
            ? { id: full.id, display_name: full.display_name }
            : { id: itemId, display_name: `ID ${itemId}` };
        })
        .filter(Boolean);
      assignedItems.sort((a: any, b: any) =>
        (a.display_name || "").localeCompare(b.display_name || "")
      );
      setUserAssignedRestrictedItems(assignedItems);
      setNewUserSelectedRestrictedItems({ granted: itemIds });
      initialPermissionsRef.current = {
        ...initialPermissionsRef.current,
        items: itemIds,
      };
    } catch (error) {
      console.error("Error fetching restricted items for user:", error);
      Toastify("error", "Error fetching restricted drawer items");
      setUserAssignedRestrictedItems([]);
      setNewUserSelectedRestrictedItems({ granted: [] });
      initialPermissionsRef.current = {
        ...initialPermissionsRef.current,
        items: [],
      };
    } finally {
      setLoadingRestrictedItems(false);
    }
  };

  const editUser = async (row: User) => {
    if (!hasEditAccess) {
      Toastify("error", "You do not have permission to edit users");
      return;
    }
    if (!row || row.id == null) {
      Toastify("error", "Cannot edit user: invalid user data");
      return;
    }
    const userId = Number(row.id);
    if (Number.isNaN(userId)) {
      Toastify("error", "Cannot edit user: invalid user id");
      return;
    }
    setUpdatingUser(row);
    setName(row.name ?? "");
    setEmail(row.email ?? "");
    setNameError("");
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");
    setDisableEmailEdit(true);
    setDisablePasswordEdit(true);
    setNewUserDrawerOpen(true);
    setAddUser(false);
    // Reset permission snapshot before loading the user's current grants
    initialPermissionsRef.current = emptyPermissionSnapshot();
    setBrandsPerUser([]);
    setUserAssignedChannels([]);
    setUserAssignedApplications([]);
    setUserAssignedRestrictedItems([]);
    setNewUserSelectedBrand({ granted: [] });
    setNewUserSelectedChannel({ granted: [] });
    setNewUserSelectedApplication({ granted: [] });
    setNewUserSelectedRestrictedItems({ granted: [] });
    void getBrandsforUser(userId);
    // Resolve app/channel/item catalogs first — they load async on page init; mapping IDs to labels
    // must not run against empty lists (race after hard refresh).
    const [channelsData, applicationsData, restrictedItemsData] = await Promise.all([
      channelList.length ? Promise.resolve(channelList) : getListOfAllChannels(),
      applicationList.length ? Promise.resolve(applicationList) : getListOfAllApplications(),
      restrictedItemList.length
        ? Promise.resolve(restrictedItemList)
        : getListOfRestrictedItems(),
    ]);
    const storedDefault =
      row.default_application_id &&
      !isExcludedFromDefaultApplication(
        (applicationsData || []).find(
          (app: any) => String(app.id) === String(row.default_application_id)
        )
      )
        ? String(row.default_application_id)
        : "";
    setDefaultApplicationId(storedDefault);
    await getChannelsforUser(userId, channelsData);
    await getApplicationsforUser(userId, applicationsData);
    await getRestrictedItemsForUser(userId, restrictedItemsData);
  };

  // Search handler: filter by name, email, brand; respect user type filter
  const searchResults = useCallback(
    (query: string) => {
      setPage(0);
      if (!query.trim()) {
        setUsers(applyUserTypeFilter(usersBackup, userTypeFilter));
        return;
      }
      const q = query.toLowerCase();
      const filteredByType = applyUserTypeFilter(usersBackup, userTypeFilter);
      setUsers(
        filteredByType.filter(
          (user: any) =>
            user.name?.toLowerCase().includes(q) ||
            user.email?.toLowerCase().includes(q) ||
            user.brand?.toLowerCase().includes(q)
        )
      );
    },
    [usersBackup, userTypeFilter]
  );

  const searchOptimized = useMemo(() => debounce(searchResults, 600), [searchResults]);

  // Handle sorting
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Sorting comparator
  const getComparator = (order: "asc" | "desc", orderBy: string) => {
    return order === "desc"
      ? (a: any, b: any) => descendingComparator(a, b, orderBy)
      : (a: any, b: any) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a: any, b: any, orderBy: string) => {
    if (orderBy === "created_at") {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    if (orderBy === "name" || orderBy === "email") {
      const va = String(a[orderBy] ?? "").toLowerCase();
      const vb = String(b[orderBy] ?? "").toLowerCase();
      return vb.localeCompare(va, undefined, { sensitivity: "base" });
    }
    if (!a[orderBy] && !b[orderBy]) return 0;
    if (!a[orderBy]) return 1;
    if (!b[orderBy]) return -1;
    if (typeof a[orderBy] === "string") {
      return b[orderBy].toLowerCase().localeCompare(a[orderBy].toLowerCase());
    }
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  const stableSort = (array: any[], comparator: (a: any, b: any) => number) => {
    const stabilizedThis = array.map((el, index) => [el, index] as [any, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  // Selection handlers
  const isSelected = (id: number) => selected.includes(id);

  const handleSelect = useCallback((id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const onHeaderSelectAllChange = useCallback(
    (value: boolean | "indeterminate") => {
      if (value === true) {
        setSelected(activeSelectableIds);
      } else {
        setSelected([]);
      }
    },
    [activeSelectableIds]
  );

  // Team change handlers
  const handleTeamChange = (user: User) => {
      setUserToModify(user);
      setConfirmTeamDialog(true);
  };

  const confirmTeamChange = async () => {
    if (!userToModify) return;
    if (!hasEditAccess) {
      Toastify("error", "You do not have permission to change team membership");
      setConfirmTeamDialog(false);
      setUserToModify(null);
      return;
    }
    setTeamDialogLoading(true);

    try {
      const before = { team: !!userToModify.team, is_admin: !!userToModify.is_admin };
      const res = await apiClient.put(`/user/v1/admin/team/${userToModify.id}`);
      const after = {
        team: !!res?.data?.response?.team,
        is_admin: !!res?.data?.response?.is_admin,
      };

      const primary = before.team ? "Removed from team" : "Added to team";
      const cascaded =
        before.is_admin !== after.is_admin
          ? after.is_admin
            ? "Admin access also granted"
            : "Admin access also removed"
          : null;

      Toastify("success", cascaded ? `${primary} (${cascaded})` : primary);
      const teamChanges = [];
      if (before.team !== after.team) {
        teamChanges.push({ label: 'Team Member', from: before.team ? 'Yes' : 'No', to: after.team ? 'Yes' : 'No' });
      }
      if (before.is_admin !== after.is_admin) {
        teamChanges.push({ label: 'Admin Access', from: before.is_admin ? 'Yes' : 'No', to: after.is_admin ? 'Yes' : 'No' });
      }
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: buildSummary(
            "User",
            userToModify.email,
            "updated",
            teamChanges.length
          ),
          status: res.status,
          method: res.config.method,
          url: res.config.url,
          category: "user_management",
          action: before.team ? "remove_team" : "add_team",
          userId: userToModify.id,
          userEmail: userToModify.email,
          changes: teamChanges,
        }),
        false
      );
      getUsers();
    } catch (error: any) {
      Toastify("error", error?.response?.data?.message || "Error updating team status");
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Failed to update team status for user "${userToModify?.email}": ${error?.response?.data?.message || error?.message || 'Unknown error'}`,
          status: error?.response?.status || 500,
          method: 'PUT',
          url: `/user/v1/admin/team/${userToModify?.id}`,
          category: 'user_management',
          userId: userToModify?.id,
        }),
        true
      );
    } finally {
      // The PUT may have committed even on error, and can cascade is_admin.
      await invalidateUserSidebarCache(userToModify.id);
      setTeamDialogLoading(false);
      setConfirmTeamDialog(false);
      setUserToModify(null);
    }
  };

  // Admin change handlers
  const handleAdminAccessChange = (user: User) => {
      setUserToModify(user);
      setConfirmAdminDialog(true);
  };

  const confirmAdminChange = async () => {
    if (!userToModify) return;
    if (!hasEditAccess) {
      Toastify("error", "You do not have permission to change admin access");
      setConfirmAdminDialog(false);
      setUserToModify(null);
      return;
    }
    setAdminDialogLoading(true);

    try {
      const before = { team: !!userToModify.team, is_admin: !!userToModify.is_admin };
      const res = await apiClient.put(`/user/v1/admin/level/${userToModify.id}`);
      const after = {
        team: !!res?.data?.response?.team,
        is_admin: !!res?.data?.response?.is_admin,
      };

      const primary = before.is_admin ? "Admin access removed" : "Admin access granted";
      const cascaded =
        before.team !== after.team
          ? after.team
            ? "Added to team as well"
            : "Removed from team as well"
          : null;

      Toastify("success", cascaded ? `${primary} (${cascaded})` : primary);
      const adminChanges = [];
      if (before.is_admin !== after.is_admin) {
        adminChanges.push({ label: 'Admin Access', from: before.is_admin ? 'Yes' : 'No', to: after.is_admin ? 'Yes' : 'No' });
      }
      if (before.team !== after.team) {
        adminChanges.push({ label: 'Team Member', from: before.team ? 'Yes' : 'No', to: after.team ? 'Yes' : 'No' });
      }
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: buildSummary(
            "User",
            userToModify.email,
            "updated",
            adminChanges.length
          ),
          status: res.status,
          method: res.config.method,
          url: res.config.url,
          category: "user_management",
          action: before.is_admin ? "remove_admin" : "add_admin",
          userId: userToModify.id,
          userEmail: userToModify.email,
          changes: adminChanges,
        }),
        false
      );
      getUsers();
    } catch (error: any) {
      Toastify("error", error?.response?.data?.message || "Error updating admin status");
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Failed to update admin status for user "${userToModify?.email}": ${error?.response?.data?.message || error?.message || 'Unknown error'}`,
          status: error?.response?.status || 500,
          method: 'PUT',
          url: `/user/v1/admin/level/${userToModify?.id}`,
          category: 'user_management',
          userId: userToModify?.id,
        }),
        true
      );
    } finally {
      // The PUT may have committed even on error, and can cascade team.
      await invalidateUserSidebarCache(userToModify.id);
      setAdminDialogLoading(false);
      setConfirmAdminDialog(false);
      setUserToModify(null);
    }
  };

  // Bulk action handlers — defer opening the confirm dialog until after the dropdown menu has
  // fully closed. Otherwise Radix can leave focus inside a popper subtree while the wrapper gets
  // aria-hidden, which breaks clicks/focus for the rest of the page (see console:
  // "Blocked aria-hidden on an element because its descendant retained focus").
  const handleBulkAction = useCallback((action: string) => {
    setBulkAction(action);
    // One macrotask after close + focus cleanup; if anything still sticks in prod, try 100–150ms.
    window.setTimeout(() => {
      setConfirmBulkDialog(true);
    }, 0);
  }, []);

  const getBulkActionCopy = (action: string) => {
    switch (action) {
      case "verify":
        return { title: "Allow users", verb: "allow", destructive: false };
      case "hold":
        return { title: "Put users on hold", verb: "put on hold", destructive: false };
      case "add-team":
        return { title: "Add to team", verb: "add to the team", destructive: false };
      case "remove-team":
        return { title: "Remove from team", verb: "remove from the team", destructive: false };
      case "add-admin":
        return { title: "Grant admin privileges", verb: "grant admin privileges to", destructive: false };
      case "remove-admin":
        return { title: "Remove admin privileges", verb: "remove admin privileges from", destructive: false };
      case "delete":
        return {
          title: "Delete users",
          verb: "permanently delete",
          destructive: true,
          extra:
            "This will remove account access. Associated data may remain stored. This cannot be undone from this dialog.",
        };
      default:
        return { title: "Bulk action", verb: "update", destructive: false };
    }
  };

  /** When bulk skips everyone (already in target state), avoid "0 users — … completed". */
  const getBulkNoChangeMessage = (action: string, count: number): string | null => {
    const n = count === 1 ? "user" : "users";
    switch (action) {
      case "add-team":
        return `No changes needed — selected ${n} ${count === 1 ? "is" : "are"} already on the team.`;
      case "remove-team":
        return `No changes needed — selected ${n} ${count === 1 ? "is" : "are"} not on the team.`;
      case "add-admin":
        return `No changes needed — selected ${n} already ${count === 1 ? "has" : "have"} admin access.`;
      case "remove-admin":
        return `No changes needed — selected ${n} ${count === 1 ? "does" : "do"} not have admin access.`;
      default:
        return null;
    }
  };

  const confirmBulkAction = async () => {
    if (!hasViewAccess) {
      Toastify("error", "You do not have permission to view User Management");
      setConfirmBulkDialog(false);
      return;
    }

    if (bulkAction === "delete" && !hasManageAccess) {
      Toastify("error", "You do not have permission to delete users");
      setConfirmBulkDialog(false);
      return;
    }

    if (
      (bulkAction === "verify" ||
        bulkAction === "hold" ||
        bulkAction === "add-team" ||
        bulkAction === "remove-team" ||
        bulkAction === "add-admin" ||
        bulkAction === "remove-admin") &&
      !hasEditAccess
    ) {
      Toastify("error", "You do not have permission to perform this action");
      setConfirmBulkDialog(false);
      return;
    }

    const selectedUsers = users.filter((u) => selected.includes(u.id));
    if (selectedUsers.length === 0) {
      Toastify("warning", "None of the selected users are in the current list. Clear selection or adjust filters.");
      setConfirmBulkDialog(false);
      return;
    }

    const onHoldUsers = selectedUsers.filter((u) => u.admin_verified === 2);
    if (onHoldUsers.length > 0 && bulkAction !== "verify") {
      Toastify(
        "warning",
        `Cannot run this bulk action: ${onHoldUsers.length} selected user${onHoldUsers.length === 1 ? " is" : "s are"} on hold. Remove them from the selection or use Allow first.`
      );
      setConfirmBulkDialog(false);
      return;
    }

    setBulkActionLoading(true);
    let successCount = 0;
    let errorCount = 0;
    const succeededEmails: string[] = [];
    const failedEmails: string[] = [];

    try {
      for (const user of selectedUsers) {
        // Team/admin toggles change what the sidebar shows; clear the user's cache once
        // after their PUT, even if it errored (the write may still have committed).
        let sidebarAffected = false;
        try {
          switch (bulkAction) {
            case "verify":
              await apiClient.post(`/user/v1/admin/verify/${user.id}`);
              successCount++;
              succeededEmails.push(user.email);
              break;
            case "hold":
              await apiClient.put(`/user/v1/admin/hold/${user.id}`);
              successCount++;
              succeededEmails.push(user.email);
              break;
            case "add-team":
              if (!user.team) {
                sidebarAffected = true;
                await apiClient.put(`/user/v1/admin/team/${user.id}`);
                successCount++;
                succeededEmails.push(user.email);
              }
              break;
            case "remove-team":
              if (user.team) {
                sidebarAffected = true;
                await apiClient.put(`/user/v1/admin/team/${user.id}`);
                successCount++;
                succeededEmails.push(user.email);
              }
              break;
            case "add-admin":
              if (!user.is_admin) {
                sidebarAffected = true;
                await apiClient.put(`/user/v1/admin/level/${user.id}`);
                successCount++;
                succeededEmails.push(user.email);
              }
              break;
            case "remove-admin":
              if (user.is_admin) {
                sidebarAffected = true;
                await apiClient.put(`/user/v1/admin/level/${user.id}`);
                successCount++;
                succeededEmails.push(user.email);
              }
              break;
            case "delete":
              await apiClient.delete(`/user/v1/admin/delete/${user.id}`);
              successCount++;
              succeededEmails.push(user.email);
              break;
            default:
              break;
          }
        } catch (e) {
          console.error(`Bulk action ${bulkAction} failed for user ${user.id}`, e);
          errorCount++;
          failedEmails.push(user.email);
        } finally {
          if (sidebarAffected) {
            await invalidateUserSidebarCache(user.id);
          }
        }
      }

      const bulkLabel = getBulkActionCopy(bulkAction).title.toLowerCase();
      if (errorCount > 0) {
        Toastify(
          "warning",
          `${successCount} users updated; ${errorCount} failed. (${bulkLabel})`
        );
      } else if (successCount === 0) {
        const noop = getBulkNoChangeMessage(bulkAction, selectedUsers.length);
        Toastify(
          noop ? "success" : "info",
          noop ?? "No changes were applied."
        );
      } else {
        Toastify(
          "success",
          `${successCount} user${successCount === 1 ? "" : "s"} — ${bulkLabel} completed`
        );
      }

      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Bulk action "${bulkLabel}" applied to ${successCount} user${successCount === 1 ? "" : "s"}`,
          category: "user_management",
          action: `bulk_${bulkAction}`,
          successCount,
          errorCount,
          details: [
            { label: "Action", value: bulkLabel },
            { label: "Users Updated", value: String(successCount) },
            { label: "Failed", value: String(errorCount) },
            {
              label: "Updated Users",
              value: succeededEmails.length
                ? succeededEmails.join(", ")
                : "None",
            },
            ...(failedEmails.length
              ? [
                  {
                    label: "Failed Users",
                    value: failedEmails.join(", "),
                  },
                ]
              : []),
          ],
        }),
        errorCount > 0
      );

      setSelected([]);
      getUsers();
    } catch (error: any) {
      Toastify("error", error?.response?.data?.message || "Error performing bulk action");
      await actionLog?.actionLogTrack(JSON.stringify(error), true);
    } finally {
      setBulkActionLoading(false);
      setConfirmBulkDialog(false);
    }
  };

  // Get status badge variant
  const getStatusBadge = (user: User) => {
    if (user.is_active === 0) {
      return <Badge variant="destructive">Deleted</Badge>;
    }
    switch (user.admin_verified) {
      case 0:
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 border-yellow-300">Pending</Badge>;
      case 1:
        return <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-300">Allowed</Badge>;
      case 2:
        return <Badge variant="secondary" className="bg-cyan-100 text-cyan-800 border-cyan-300">On Hold</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  // DataTable columns (lift-ui) - memoized to prevent infinite loops
  const dataTableColumns: TableColumn<User>[] = useMemo(() => {
    const isDeletedOnlyView =
      userTypeFilter.length === 1 && userTypeFilter[0] === "deleted";
    const showSelectColumn = hasEditAccess && !isDeletedOnlyView;
    // IAM: Editor+ for row actions (Allow/Hold/Edit); Delete buttons still require Manager (hasManageAccess) inside the cell
    const showActionsColumn = hasEditAccess && !isDeletedOnlyView;
    const showPermissionsColumn = hasEditAccess && !isDeletedOnlyView;

    const baseColumns: TableColumn<User>[] = [];

    if (showSelectColumn) {
      const selectedActiveCount = selected.filter((id) =>
        activeSelectableIds.includes(id)
      ).length;
      const headerChecked: boolean | "indeterminate" =
        activeSelectableIds.length > 0 &&
        selectedActiveCount === activeSelectableIds.length
          ? true
          : selectedActiveCount > 0
            ? "indeterminate"
            : false;

      baseColumns.push({
        key: "select",
        header: (
          <Checkbox
            checked={headerChecked}
            onCheckedChange={onHeaderSelectAllChange}
            aria-label="Select all users on this list"
          />
        ),
        accessor: (row) => row.id,
        width: "50px",
        render: (_value, row) =>
          row.is_active !== 0 ? (
            <Checkbox
              checked={isSelected(row.id)}
              onCheckedChange={(checked) => {
                const sel = selected.includes(row.id);
                if (Boolean(checked) !== sel) handleSelect(row.id);
              }}
              aria-label={`Select ${row.name}`}
            />
          ) : null,
      });
    }

    baseColumns.push(
      {
        key: "name",
        header: "NAME",
        accessor: "name",
        sortable: true,
        width: "15%",
        render: (_value, row) => (
          <span className="font-medium">{row.name}</span>
        ),
      },
      {
        key: "email",
        header: "EMAIL",
        accessor: "email",
        sortable: true,
        width: "20%",
        render: (_value, row) => (
          <div className="flex items-center gap-2">
            <span>{row.email}</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    {row.verified ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {row.verified ? "Email Verified" : "Email not verified"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
      },
      {
        key: "created_at",
        header: "CREATED AT",
        accessor: "created_at",
        sortable: true,
        width: "15%",
        render: (value) => formatDate(value),
      }
    );

    if (showActionsColumn) {
      baseColumns.push({
        key: "actions",
        header: "ACTIONS",
        accessor: (row) => row.id,
        width: "auto",
        align: "center",
        render: (_value, user) => {
          if (user.is_active === 0) return null;
          const actionButtons = [];
          if (hasEditAccess && (user.admin_verified === 0 || user.admin_verified === 2)) {
            actionButtons.push(
              <TooltipProvider key="allow">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => { setUserToAllow(user); setConfirmAllowDialog(true); }} className="h-8 w-8 p-0">
                      <Check className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Allow</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
          if (hasEditAccess && (user.admin_verified === 0 || user.admin_verified === 1)) {
            actionButtons.push(
              <TooltipProvider key="hold">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => { setUserToHold(user); setConfirmHoldDialog(true); }} className="h-8 w-8 p-0">
                      <Pause className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Hold</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
          if (hasEditAccess) {
            actionButtons.push(
              <TooltipProvider key="edit">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => editUser(user)} className="h-8 w-8 p-0">
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
          if (hasManageAccess && (user.admin_verified === 0 || user.admin_verified === 1 || user.admin_verified === 2)) {
            actionButtons.push(
              <TooltipProvider key="delete">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => setAboutToDeleteUsers(user)} className="h-8 w-8 p-0 text-red-600 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
          return <div className="flex items-center justify-center gap-1">{actionButtons}</div>;
        },
      });
    }

    baseColumns.push({
      key: "admin_verified",
      header: "STATUS",
      accessor: "admin_verified",
      width: "auto",
      align: "center",
      render: (_value, row) => getStatusBadge(row),
    });

    if (showPermissionsColumn) {
      baseColumns.push({
        key: "permissions",
        header: "PERMISSIONS",
        accessor: (row) => row.id,
        width: "auto",
        align: "center",
        render: (_value, user) => {
          if (user.is_active === 0) return null;
          return (
            <div className="flex items-center justify-center gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => handleTeamChange(user)} className="h-8 w-8 p-0" disabled={loaderAddToTeam[user.id]}>
                      {loaderAddToTeam[user.id] ? <SpinnerLoader className="h-4 w-4" /> : <Users2 className={`h-4 w-4 ${user.team ? "text-blue-600 fill-blue-100" : "text-gray-400"}`} />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{user.team ? "Remove from Team" : "Add to Team"}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => handleAdminAccessChange(user)} className="h-8 w-8 p-0" disabled={loaderAdmin[user.id]}>
                      {loaderAdmin[user.id] ? <SpinnerLoader className="h-4 w-4" /> : <ShieldCheck className={`h-4 w-4 ${user.is_admin ? "text-blue-600 fill-blue-100" : "text-gray-400"}`} />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{user.is_admin ? "Admin" : "Not an Admin"}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [
    activeSelectableIds,
    selected,
    onHeaderSelectAllChange,
    handleSelect,
    loaderAddToTeam,
    loaderAdmin,
    hasEditAccess,
    hasManageAccess,
    userTypeFilter,
    users,
  ]);

  // Paginated and sorted data - memoized to prevent infinite loops
  const sortedData = useMemo(
    () => stableSort(users, getComparator(order, orderBy)),
    [users, order, orderBy]
  );

  const paginatedData = useMemo(
    () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedData, page, rowsPerPage]
  );

  const openAddUserDrawer = async () => {
    if (!hasEditAccess) {
      Toastify("error", "You do not have permission to add users");
      return;
    }
    setName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setNameError("");
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");
    setUpdatingUser("");
    setDefaultApplicationId(DEFAULT_CREATE_USER_APPLICATION_ID);
    setNewUserDrawerOpen(true);
    setDisableEmailEdit(false);
    setDisablePasswordEdit(false);
    setAddUser(true);
    setChannelAccessWarnings([]);
    setApplicationAccessWarnings([]);
    // Deny-by-default: never pre-grant restricted drawer items on create
    setUserAssignedRestrictedItems([]);
    setNewUserSelectedRestrictedItems({ granted: [] });

    // Match main: pre-select "Lift Demo Brand" for new users
    const liftDemoBrand = (allBrandsList || [])
      .flatMap((org: any) => org.brands || [])
      .find((b: any) => b?.name === "Lift Demo Brand" || b?.name === "LIFT Demo Brand");
    if (liftDemoBrand) {
      setBrandsPerUser([liftDemoBrand]);
      setNewUserSelectedBrand({ granted: [String(liftDemoBrand.id)] });
    } else {
      setBrandsPerUser([]);
      setNewUserSelectedBrand([]);
    }

    const [chList, appList] = await Promise.all([
      channelList.length ? Promise.resolve(channelList) : getListOfAllChannels(),
      applicationList.length ? Promise.resolve(applicationList) : getListOfAllApplications(),
    ]);

    // Pre-select only the default application (matches backend initializeUser)
    const defaultApp = (appList || []).find(
      (app: any) =>
        app?.id != null &&
        String(app.id) === DEFAULT_CREATE_USER_APPLICATION_ID
    );
    const defaultAppIds = defaultApp ? [String(defaultApp.id)] : [];
    setUserAssignedApplications(defaultApp ? [defaultApp] : []);
    setNewUserSelectedApplication({ granted: defaultAppIds });
    setDefaultApplicationId(defaultApp ? String(defaultApp.id) : "");

    // Pre-select only channels belonging to that application
    const appChannels = (chList || []).filter(
      (ch: any) => ch?.id != null && String(ch.application_id) === DEFAULT_CREATE_USER_APPLICATION_ID
    );
    const appChannelIds = appChannels.map((ch: any) => String(ch.id));
    setUserAssignedChannels(appChannels);
    setNewUserSelectedChannel({ granted: appChannelIds });
  };

  if (authLoading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center min-h-[320px] bg-white gap-3">
        <SpinnerLoader className="h-8 w-8" />
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!hasViewAccess) {
    return (
      <div className="flex flex-col h-full bg-white px-8 py-8">
        <h1 className="text-xl font-semibold text-gray-900 mb-2">{title}</h1>
        <p className="text-muted-foreground max-w-lg">
          You don&apos;t have permission to view User Management. Ask an administrator for{" "}
          <span className="font-medium text-foreground">user_mgmt</span> at Viewer or above (IAM: Viewer =
          read-only, Editor = add/update, Manager = delete included).
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Page Header */}
      <div className="flex items-center justify-between px-8 py-4 border-b bg-white gap-4">
        <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        <div className="flex items-center gap-3 flex-1 justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={getUsers}
            disabled={loaderUsers}
            className="flex items-center gap-2 h-8 px-3"
          >
            {loaderUsers ? (
              <SpinnerLoader className="h-4 w-4" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </Button>
          {hasEditAccess && (
            <Button
              onClick={openAddUserDrawer}
              size="sm"
              className="flex items-center gap-2 h-8 px-3"
            >
              + Add Users
            </Button>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search users..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                searchOptimized(e.target.value);
              }}
              className="pl-10 w-80"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 px-8 py-6">
        <div className="flex-1 flex flex-col min-h-0">
          {/* Filter row — aligned with drawer-structure Reports / MultiSelect pattern */}
          <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
            <div className="flex flex-wrap items-end gap-4 shrink-0">
              <div className="space-y-1.5 w-[300px] max-w-full shrink-0">
                <MultiSelectCheckboxTag
                  placeholder={
                    userTypeFilter.length > 0
                      ? `Filters (${userTypeFilter.length})`
                      : "All users"
                  }
                  options={USER_TYPE_FILTER_OPTIONS}
                  value={userTypeFilter}
                  onChange={onUserTypesFilterChange}
                  containerClassName="w-full min-w-0"
                  showSelectAll
                />
              </div>
            </div>

            {hasEditAccess &&
            selected.length > 0 &&
            !(userTypeFilter.length === 1 && userTypeFilter[0] === "deleted") ? (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 px-3 py-1.5 sm:ml-auto">
                <p className="text-sm font-medium text-foreground whitespace-nowrap">
                  {selected.length} user{selected.length === 1 ? "" : "s"} selected
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8"
                    onClick={() => setSelected([])}
                  >
                    Clear selection
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="default" size="sm" className="h-8 gap-2">
                        Bulk actions
                        <MoreVertical className="h-4 w-4 opacity-70" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="min-w-80 w-80 [&_[role=menuitem]]:whitespace-nowrap"
                      onCloseAutoFocus={(e) => e.preventDefault()}
                    >
                      <DropdownMenuItem onSelect={() => handleBulkAction("verify")}>
                        <Check className="mr-2 h-4 w-4 text-green-600" />
                        Allow users
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => handleBulkAction("hold")}>
                        <Pause className="mr-2 h-4 w-4 text-cyan-600" />
                        Put on hold
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => handleBulkAction("add-team")}>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Add to team
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => handleBulkAction("remove-team")}>
                        <UserMinus className="mr-2 h-4 w-4" />
                        Remove from team
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => handleBulkAction("add-admin")}>
                        <ShieldCheck className="mr-2 h-4 w-4" />
                        Grant admin
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => handleBulkAction("remove-admin")}>
                        <ShieldOff className="mr-2 h-4 w-4" />
                        Remove admin
                      </DropdownMenuItem>
                      {hasManageAccess && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onSelect={() => handleBulkAction("delete")}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete users
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex-1 min-h-0">
            <DataTable<User>
              className="flex-1 min-h-0 overflow-auto"
              maxHeight="calc(100vh - 200px)"
              columns={dataTableColumns}
              data={paginatedData}
              loading={loadList || tableLoader}
              emptyMessage="No users found."
              stickyHeader
              stickyFooter
              onSort={(key, direction) => {
                setOrder(direction);
                setOrderBy(key);
              }}
              sortConfig={orderBy ? { key: orderBy, direction: order } : undefined}
              pagination={{
                currentPage: page + 1,
                totalPages: Math.max(1, Math.ceil(sortedData.length / rowsPerPage)),
                pageSize: rowsPerPage,
                totalItems: sortedData.length,
                onPageChange: (p) => handlePageChange(p - 1),
                onPageSizeChange: handlePageSizeChange,
                pageSizeOptions: [15, 25, 50, 100],
              }}
              showActions={false}
            />
          </div>
        </div>
      </div>

      {/* Add/Edit User Sheet */}
      <Sheet
        open={newUserDrawerOpen}
        onOpenChange={(open) => {
          setNewUserDrawerOpen(open);
          if (!open) {
            setAddUser(false);
            setUpdatingUser(undefined);
            setName("");
            setEmail("");
            setPassword("");
            setConfirmPassword("");
            setNameError("");
            setEmailError("");
            setPasswordError("");
            setConfirmPasswordError("");
            setDisableEmailEdit(false);
            setDisablePasswordEdit(false);
            setBrandsPerUser([]);
            setUserAssignedChannels([]);
            setUserAssignedApplications([]);
            setUserAssignedRestrictedItems([]);
            setNewUserSelectedBrand([]);
            setNewUserSelectedChannel([]);
            setNewUserSelectedApplication([]);
            setNewUserSelectedRestrictedItems({ granted: [] });
            initialPermissionsRef.current = emptyPermissionSnapshot();
            setChannelAccessWarnings([]);
            setApplicationAccessWarnings([]);
          }
        }}
      >
        <SheetContent className="w-[500px] sm:max-w-[500px] flex flex-col">
          <SheetHeader>
            <SheetTitle>{disableEmailEdit ? "Update User" : "Add New User"}</SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto py-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
              type="text"
                placeholder="Enter name"
              value={name}
                onChange={(e) => setName(e.target.value)}
                className={nameError ? "border-destructive" : ""}
              />
              {nameError && (
                <p className="text-sm text-destructive">{nameError}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
              type="email"
                placeholder="Enter email"
              value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={disableEmailEdit}
                className={emailError ? "border-destructive" : ""}
              />
              {emailError && (
                <p className="text-sm text-destructive">{emailError}</p>
              )}
            </div>

            {!disablePasswordEdit && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
              type="password"
                    placeholder="Enter password"
              value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={passwordError ? "border-destructive" : ""}
                  />
                  {passwordError && (
                    <p className="text-sm text-destructive">{passwordError}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
              type="password"
                    placeholder="Confirm password"
              value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={confirmPasswordError ? "border-destructive" : ""}
                  />
                  {confirmPasswordError && (
                    <p className="text-sm text-destructive">{confirmPasswordError}</p>
                  )}
                    </div>
              </>
                  )}

            {addUser && (
              <p className="text-sm text-muted-foreground">
                Note: The new user gets these permissions by default, it can be updated later
              </p>
            )}

            <div className="space-y-2">
              <Label>Brands {loadingBrands && <span className="text-xs text-muted-foreground ml-2">Fetching Brands...</span>}</Label>
              <MultiSelectCheckboxTag
                placeholder="Select brands"
                options={useMemo(() => {
                  const brandOptions: { label: string; value: string }[] = [];
                  (allBrandsList || []).forEach((org: any) => {
                    (org.brands || []).forEach((brand: any) => {
                      if (brand?.id != null) {
                        brandOptions.push({
                          label: String(brand.name ?? ""),
                          value: String(brand.id),
                        });
                      }
                    });
                  });
                  return brandOptions;
                }, [allBrandsList])}
                value={useMemo(() => {
                  if (!brandsPerUser || !Array.isArray(brandsPerUser)) return [];
                  return brandsPerUser
                    .filter((b: any) => b?.id != null)
                    .map((b: any) => String(b.id));
                }, [brandsPerUser])}
                onChange={(selectedValues: string[]) => {
                  const selectedBrands = (allBrandsList || [])
                    .flatMap((org: any) => org.brands || [])
                    .filter((brand: any) => brand?.id != null && selectedValues.includes(String(brand.id)));
                  setBrandsPerUser(selectedBrands);
                  setNewUserSelectedBrand({ granted: selectedValues });
                }}
                state={addUser ? "disabled" : undefined}
                containerClassName="w-full"
                showSelectAll
              />
            </div>

            <div className="space-y-2">
              <Label>Applications {loadingApplications && <span className="text-xs text-muted-foreground ml-2">Fetching Applications...</span>}</Label>
              <MultiSelectCheckboxTag
                placeholder="Select applications"
                options={(applicationList || [])
                  .filter((app: any) => app?.id != null)
                  .map((app: any) => ({
                    label: String(app.name ?? ""),
                    value: String(app.id),
                  }))}
                value={useMemo(() => {
                  if (!userAssignedApplications || !Array.isArray(userAssignedApplications)) return [];
                  return userAssignedApplications
                    .filter((app: any) => app?.id != null)
                    .map((app: any) => String(app.id));
                }, [userAssignedApplications])}
                onChange={(selectedValues: string[]) => {
                  const selectedApps = (applicationList || []).filter((app: any) =>
                    app?.id != null && selectedValues.includes(String(app.id))
                  );
                  setUserAssignedApplications(selectedApps);
                  setNewUserSelectedApplication({ granted: selectedValues });
                  setDefaultApplicationId((current) =>
                    selectedValues.includes(current)
                      ? current
                      : ""
                  );
                }}
                state={addUser ? "disabled" : undefined}
                containerClassName="w-full"
                showSelectAll
              />
            </div>

            <div className="space-y-2">
              <Label>
                Channels{" "}
                {loadingChannels && <span className="text-xs text-muted-foreground ml-2">Fetching Channels...</span>}
                {(!userAssignedApplications || (Array.isArray(userAssignedApplications) && userAssignedApplications.length === 0)) && (
                  <span className="text-xs text-muted-foreground ml-2">Select an application first</span>
                )}
              </Label>
              <MultiSelectCheckboxTag
                placeholder={
                  !userAssignedApplications || (Array.isArray(userAssignedApplications) && userAssignedApplications.length === 0)
                    ? "Select application(s) first"
                    : "Select channels"
                }
                options={(channelList || [])
                  .filter((ch: any) => ch?.id != null)
                  .filter((ch: any) => {
                    const selectedAppIds = (userAssignedApplications || [])
                      .filter((a: any) => a?.id != null)
                      .map((a: any) => String(a.id));
                    if (selectedAppIds.length === 0) return false;
                    return selectedAppIds.includes(String(ch?.application_id));
                  })
                  .map((channel: any) => ({
                    label: channel.application_name
                      ? `${channel.name ?? ""} (${channel.application_name})`
                      : String(channel.name ?? ""),
                    value: String(channel.id),
                  }))}
                value={useMemo(() => {
                  if (!userAssignedChannels || !Array.isArray(userAssignedChannels)) return [];
                  return userAssignedChannels
                    .filter((ch: any) => ch?.id != null)
                    .map((ch: any) => String(ch.id));
                }, [userAssignedChannels])}
                onChange={(selectedValues: string[]) => {
                  const selectedChannels = (channelList || []).filter((channel: any) =>
                    channel?.id != null && selectedValues.includes(String(channel.id))
                  );
                  setUserAssignedChannels(selectedChannels);
                  setNewUserSelectedChannel({ granted: selectedValues });
                }}
                state={
                  addUser || !userAssignedApplications || (Array.isArray(userAssignedApplications) && userAssignedApplications.length === 0)
                    ? "disabled"
                    : applicationChannelError
                      ? "error"
                      : undefined
                }
                error={applicationChannelError || undefined}
                containerClassName="w-full"
                showSelectAll
              />
            </div>

            <div className="space-y-2">
              <Label>Default application</Label>
              <Select
                value={defaultApplicationId}
                onValueChange={setDefaultApplicationId}
                disabled={
                  !Array.isArray(userAssignedApplications) ||
                  !userAssignedApplications.some(
                    (app: any) =>
                      !isExcludedFromDefaultApplication(
                        (applicationList || []).find(
                          (candidate: any) =>
                            String(candidate?.id) === String(app?.id)
                        )
                      )
                  )
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a default application" />
                </SelectTrigger>
                <SelectContent>
                  {(userAssignedApplications || [])
                    .filter((app: any) => app?.id != null)
                    .filter(
                      (app: any) =>
                        !isExcludedFromDefaultApplication(
                          (applicationList || []).find(
                            (candidate: any) =>
                              String(candidate?.id) === String(app.id)
                          )
                        )
                    )
                    .map((app: any) => (
                      <SelectItem key={String(app.id)} value={String(app.id)}>
                        {String(app.name ?? app.id)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The application this user lands on after signing in.
              </p>
            </div>

            <div className="space-y-2">
              <Label>
                Restricted Drawer Items{" "}
                {loadingRestrictedItems && (
                  <span className="text-xs text-muted-foreground ml-2">
                    Fetching Restricted Items...
                  </span>
                )}
              </Label>
              {!addUser && (
                <p className="text-xs text-muted-foreground">
                  Only items marked restricted appear here. Visibility is deny-by-default —
                  grant access only to hand-picked users.
                </p>
              )}
              <MultiSelectCheckboxTag
                placeholder={
                  restrictedItemList.length === 0
                    ? "No restricted items available"
                    : "Select restricted drawer items"
                }
                options={(restrictedItemList || [])
                  .filter((item: any) => item?.id != null)
                  .map((item: any) => ({
                    label: String(item.display_name ?? item.id),
                    value: String(item.id),
                  }))}
                value={useMemo(() => {
                  if (
                    !userAssignedRestrictedItems ||
                    !Array.isArray(userAssignedRestrictedItems)
                  ) {
                    return [];
                  }
                  return userAssignedRestrictedItems
                    .filter((item: any) => item?.id != null)
                    .map((item: any) => String(item.id));
                }, [userAssignedRestrictedItems])}
                onChange={(selectedValues: string[]) => {
                  const selectedItems = (restrictedItemList || []).filter(
                    (item: any) =>
                      item?.id != null &&
                      selectedValues.includes(String(item.id))
                  );
                  setUserAssignedRestrictedItems(selectedItems);
                  setNewUserSelectedRestrictedItems({ granted: selectedValues });
                }}
                state={
                  addUser || restrictedItemList.length === 0
                    ? "disabled"
                    : undefined
                }
                containerClassName="w-full"
                showSelectAll
              />
            </div>
                </div>

          <SheetFooter className="border-t pt-4">
            <Button
              variant="outline"
              onClick={() => setNewUserDrawerOpen(false)}
                      >
              Cancel
            </Button>
            <Button
              onClick={disableEmailEdit ? () => setConfirmUpdateSheetDialog(true) : () => setConfirmCreateSheetDialog(true)}
              disabled={showLoading || loaderAdd || loaderUpdate || applicationChannelErrors.length > 0}
              className="bg-primary hover:bg-primary/90"
            >
              {showLoading || loaderAdd || loaderUpdate ? (
                <div className="flex items-center gap-2">
                  <SpinnerLoader className="h-4 w-4" />
                  Loading...
                </div>
              ) : disableEmailEdit ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete User Dialog */}
      <Dialog open={aboutToDeleteUsers !== null} onOpenChange={() => setAboutToDeleteUsers(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Deleting this user will remove their account credentials, preventing them from logging in.
              However, all relevant data will remain stored in the database.
              Are you sure you want to proceed with deleting this user?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAboutToDeleteUsers(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => aboutToDeleteUsers && throttleDeleteUser(aboutToDeleteUsers)}
              disabled={!hideDeleteUsersProgressDialog}
            >
              {!hideDeleteUsersProgressDialog ? (
                <SpinnerLoader className="h-4 w-4" />
              ) : (
                "Yes, Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allow User Dialog */}
      <Dialog open={confirmAllowDialog} onOpenChange={(open) => { if (!open) setUserToAllow(null); setConfirmAllowDialog(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Allow User</DialogTitle>
            <DialogDescription>
              Are you sure you want to allow {userToAllow?.name} ({userToAllow?.email})? They will be able to access the platform.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmAllowDialog(false); setUserToAllow(null); }}>
              Cancel
            </Button>
            <Button onClick={confirmAllowUser} disabled={allowDialogLoading}>
              {allowDialogLoading ? <SpinnerLoader className="h-4 w-4" /> : "Yes, Allow"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hold User Dialog */}
      <Dialog open={confirmHoldDialog} onOpenChange={(open) => { if (!open) setUserToHold(null); setConfirmHoldDialog(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Put User on Hold</DialogTitle>
            <DialogDescription>
              Are you sure you want to put {userToHold?.name} ({userToHold?.email}) on hold? They will not be able to access the platform until allowed again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmHoldDialog(false); setUserToHold(null); }}>
              Cancel
            </Button>
            <Button onClick={confirmHoldUser} disabled={holdDialogLoading}>
              {holdDialogLoading ? <SpinnerLoader className="h-4 w-4" /> : "Yes, Put on Hold"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team Change Dialog */}
      <Dialog open={confirmTeamDialog} onOpenChange={setConfirmTeamDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{userToModify?.team ? "Remove from Team" : "Add to Team"}</DialogTitle>
            <DialogDescription>
              {userToModify?.team
                ? `Are you sure you want to remove ${userToModify?.name} from the team?`
                : `Are you sure you want to add ${userToModify?.name} to the team?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmTeamDialog(false)}>
              Cancel
            </Button>
          <Button
              onClick={confirmTeamChange}
              disabled={teamDialogLoading}
            >
              {teamDialogLoading ? <SpinnerLoader className="h-4 w-4" /> : "Confirm"}
          </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Change Dialog */}
      <Dialog open={confirmAdminDialog} onOpenChange={setConfirmAdminDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{userToModify?.is_admin ? "Remove Admin Access" : "Grant Admin Access"}</DialogTitle>
            <DialogDescription>
              {userToModify?.is_admin
                ? `Are you sure you want to remove admin privileges from ${userToModify?.name}?`
                : `Are you sure you want to grant admin privileges to ${userToModify?.name}?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAdminDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmAdminChange}
              disabled={adminDialogLoading}
            >
              {adminDialogLoading ? <SpinnerLoader className="h-4 w-4" /> : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog
        open={confirmBulkDialog}
        onOpenChange={(open) => {
          if (!open && !bulkActionLoading) setConfirmBulkDialog(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{getBulkActionCopy(bulkAction).title}</DialogTitle>
            <DialogDescription className="space-y-2">
              <span>
                Are you sure you want to {getBulkActionCopy(bulkAction).verb} {selected.length} selected user
                {selected.length === 1 ? "" : "s"}?
              </span>
              {getBulkActionCopy(bulkAction).extra ? (
                <span className="block font-medium text-destructive">
                  {getBulkActionCopy(bulkAction).extra}
                </span>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmBulkDialog(false)}
              disabled={bulkActionLoading}
            >
              Cancel
            </Button>
            <Button
              variant={getBulkActionCopy(bulkAction).destructive ? "destructive" : "default"}
              onClick={confirmBulkAction}
              disabled={bulkActionLoading}
            >
              {bulkActionLoading ? <SpinnerLoader className="h-4 w-4" /> : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Update User (Sheet) Dialog */}
      <Dialog open={confirmUpdateSheetDialog} onOpenChange={setConfirmUpdateSheetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Changes</DialogTitle>
            <DialogDescription>
              Are you sure you want to save changes to {updatingUser?.name} ({updatingUser?.email})? This will update their profile and permissions.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmUpdateSheetDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmUpdateSheetDialog(false);
                handleUpdate();
              }}
              disabled={showLoading || loaderAdd || loaderUpdate || applicationChannelErrors.length > 0}
            >
              Yes, Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Create User (Sheet) Dialog */}
      <Dialog open={confirmCreateSheetDialog} onOpenChange={setConfirmCreateSheetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>
              Are you sure you want to create a new user with email {email}? They will receive access based on the permissions you selected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCreateSheetDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmCreateSheetDialog(false);
                handleSubmit();
              }}
              disabled={showLoading || loaderAdd || loaderUpdate}
            >
              Yes, Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
                            </div>
  );
};

export default Users;

